import { message, StepProps } from 'antd';
import { action, computed, makeAutoObservable, observable, runInAction } from 'mobx';
import DictionaryTypes from '../../models/dictionary-types';
import ITask from '../../models/task';
import Subjects from '../../models/subjects';
import TicketTypes from '../../models/ticket-type';
import DictionariesService from '../../services/dictionaries';
import FilesService from '../../services/files-service';
import { TicketService, NewTicketRequest, FinishTicketRequest } from '../../services/ticket-service';
import TicketBar from './ticket-bar';
import React from 'react';
import NewTicketTaskItem from '../../components/ticket/new-ticket-task-item';

export default class NewTicket {

    subject: Subjects;
    filesService: FilesService;
    ticketService: TicketService;
    dictionariesService: DictionariesService;

    @observable
    id?: number | null;

    @observable
    type: TicketTypes;

    @observable
    totalTaskCount: number;

    @observable
    thematicTaskCount: number;

    @observable
    name: string;

    @observable
    egeTaskCount: number;

    @observable
    currentTaskCount: number;

    @observable
    tasks: ITask[];

    @observable
    loading: boolean;

    @observable
    pdfLink: string | null;

    constructor(subject: Subjects) {

        this.subject = subject;
        this.filesService = new FilesService();
        this.ticketService = new TicketService(subject);
        this.dictionariesService = new DictionariesService();

        this.id = null;
        this.type = TicketTypes.EGE;
        this.egeTaskCount = 0;
        this.thematicTaskCount = 5;
        this.name = '';
        this.totalTaskCount = 0;
        this.currentTaskCount = 0;
        this.tasks = [];
        this.loading = false;
        this.pdfLink = null;

        makeAutoObservable(this);
    }

    @action
    loadFull(ticketId?: number | null | undefined): void {

        this.loadDictionary();

        if (ticketId) {
            this.loadFullTicket(ticketId);
        } else {

            this.ticketService.loadCurrent(
                data => {
                    if (data?.id) {
                        this.loadFullTicket(data.id);
                    } else {
                        this.totalTaskCount = this.type === TicketTypes.EGE ? this.egeTaskCount : this.thematicTaskCount;
                    }
                }
            );
        }
    }

    //TODO copy paste >_<
    loadDictionary = async () => {
        try {
            const data = await this.dictionariesService.getDictionary(this.subject, DictionaryTypes.numbersInKim)
            runInAction(() => {
                this.egeTaskCount = (data as []).length;
                this.totalTaskCount = this.type === TicketTypes.EGE ? this.egeTaskCount : this.thematicTaskCount;
            });
        } catch (error) {
            runInAction(() => { message.error(error) });
        }
    }

    private loadFullTicket(ticketId: number) {
        try {
            this.ticketService.loadFull(
                ticketId,
                (data) => {
                    if (data?.id) {
                        this.id = data['id'];
                        this.type = data['ticketType'];
                        this.name = data['name'];
                        this.totalTaskCount = data['taskCount'];
                        this.currentTaskCount = data['currentTaskCount'];
                        this.tasks = data['tasks'];
                        this.pdfLink = data['pdf'] ? this.filesService.getFileUrl(data['pdf']) : null;

                        if (this.type === TicketTypes.THEMATIC) {
                            this.thematicTaskCount = this.totalTaskCount;
                        }
                    }
                }
            );
        } catch (error) {
            runInAction(() => { message.error(error); });
        }
    }

    @computed
    getTasksAsSteps(): StepProps[] {

        if (this.type === TicketTypes.EGE) {

            return this.range(0, this.egeTaskCount)
                .map((indx) => {

                    const task = this.tasks.find(t => t.numberInKim.name === String(indx));
                    if (task) {
                        return {
                            status: "process",
                            title: `${task.numberInKim.name}. Задача №${task.id}`,
                            description: React.createElement(NewTicketTaskItem, { task, subject: this.subject })
                        } as StepProps;
                    } else {
                        return { status: "wait", title: `${indx}.` } as StepProps;
                    }
                });

        } else {

            const mappedTasks = this.mapTasksToStepProps(this.tasks);
            console.log(`${mappedTasks.length} ${this.thematicTaskCount}`);
            const emptyTasks = mappedTasks.length === this.thematicTaskCount ? [] : this.range(mappedTasks.length, this.thematicTaskCount)
                .map((indx) => { return { status: "wait", title: `${indx}.` } as StepProps });

            return [...mappedTasks, ...emptyTasks];
        }
    }

    range(start: number, end: number): number[] {
        return end < start ? [] : Array.from(Array(end - start).keys()).map(x => x + start + 1);
    }

    mapTasksToStepProps(tasks: ITask[]): StepProps[] {
        return tasks?.slice().sort((t1, t2) => t1.id - t2.id).map((t, indx) => {
            return {
                status: "process",
                title: `${indx + 1}. Задача №${t.id}`,
                description: React.createElement(NewTicketTaskItem, { task: t, subject: this.subject })
            } as StepProps
        });
    }

    @action
    createNewTicket(mathTicketBar: TicketBar) {

        this.loading = true;

        try {

            const params: NewTicketRequest = {
                ticketType: this.type,
                count: this.totalTaskCount,
                name: this.name
            }

            this.ticketService.createTicket(
                params,
                data => {
                    if (data?.id) {
                        this.id = data.id;
                        mathTicketBar.update(data.id, 0, data.taskCount);
                    }

                    this.loading = false;
                },
                (error: string) => {
                    this.loading = false;
                    message.error(`Ошибка при создании варианта: ${error}`);
                }
            );
        } catch (error) {
            runInAction(() => {
                this.loading = false;
                message.error(`Ошибка при создании варианта: ${error}`);
            });
        }
    }

    @action
    reset = async () => {

        if (this.id) {
            this.loading = true;

            try {

                this.ticketService.reset(
                    this.id,
                    () => {
                        this.currentTaskCount = 0;
                        this.tasks = [];
                        this.loading = false;
                    },
                    (error: string) => {
                        this.loading = false;
                        message.error(`Ошибка при сбросе данных: ${error}`);
                    }
                );
            } catch (error) {
                runInAction(() => {
                    this.loading = false;
                    message.error(`Ошибка при сбросе данных: ${error}`);
                });
            }
        }
    }

    @action
    finish = async () => {

        if (this.id) {
            this.loading = true;

            if (this.currentTaskCount !== 0 && this.type === TicketTypes.EGE && this.currentTaskCount !== this.egeTaskCount) {
                message.warning('Ещё не добавлены все задачи для ЕГЭ!');
                return;
            }

            try {

                const params: FinishTicketRequest = {
                    count: this.totalTaskCount,
                    name: this.name
                }

                this.ticketService.finish(
                    this.id,
                    params,
                    () => {
                        this.id = null;
                        this.currentTaskCount = 0;
                        this.totalTaskCount = 5;
                        this.tasks = [];
                        this.loading = false;
                        this.name = '';

                        message.success('Формирование варианта успешно завершено');
                    },
                    (error: string) => {
                        this.loading = false;
                        message.error(`Ошибка при завершении формирования варианта: ${error}`);
                    }
                );
            } catch (error) {
                runInAction(() => {
                    this.loading = false;
                    message.error(`Ошибка при завершении формирования варианта: ${error}`);
                });
            }
        }
    }

    @action
    changeTicketType(type: TicketTypes): void {

        this.type = type;

        if (type === TicketTypes.EGE) {
            this.totalTaskCount = this.egeTaskCount;
        } else {
            this.totalTaskCount = this.thematicTaskCount;
        }
    }

    @action
    changeThematicCount(count: number): void {

        if (count >= this.currentTaskCount) {
            this.thematicTaskCount = count;
            this.totalTaskCount = count;
        } else {
            message.error("Нельзя установить значение меньше количества добавленных задач");
        }
    }

    @action
    changeName(value: string) {
        this.name = value;
    }

    @computed
    contains(id: number): boolean {
        return this.tasks.some(t => t.id === id);
    }

    @action
    addTaskInList(taskId: number, mathTicketBar: TicketBar): void {

        if (this.id) {
            this.loading = true;
            try {
                this.ticketService.addTaskInList(
                    this.id, taskId,
                    (data: any) => {
                        this.tasks.push(data.task as ITask);
                        this.currentTaskCount += 1;

                        mathTicketBar.update(this.id, this.currentTaskCount, this.totalTaskCount);
                    },
                    (error: string) => {
                        this.loading = false;
                        message.error(`Ошибка при добавлении задачи в вариант: ${error}`);
                    }
                );
            } catch (error) {
                runInAction(() => {
                    this.loading = false;
                    message.error(`Ошибка при добавлении задачи в вариант: ${error}`);
                });
            }
        }
    }

    @action
    deleteTaskFromList(taskId: number, mathTicketBar: TicketBar): void {

        if (this.id) {
            this.loading = true;
            try {
                this.ticketService.deleteTaskInList(
                    this.id, taskId,
                    () => {
                        this.tasks = this.tasks.filter(t => t.id !== taskId);
                        this.currentTaskCount -= 1;

                        mathTicketBar.update(this.id, this.currentTaskCount, this.totalTaskCount);
                    },
                    (error: string) => {
                        this.loading = false;
                        message.error(`Ошибка при удалении задачи из варианта: ${error}`);
                    }
                );
            } catch (error) {
                runInAction(() => {
                    this.loading = false;
                    message.error(`Ошибка при удалении задачи из варианта: ${error}`);
                });
            }
        }
    }

    @action
    makePdf(): void {
        if (this.id) {
            this.loading = true;
            try {
                this.ticketService.makePdf(
                    this.id,
                    data => {
                        this.loading = false;
                        this.pdfLink = this.filesService.getFileUrl(data["id"]);
                    },
                    (error: string) => {
                        this.loading = false;
                        message.error(`Ошибка при формировании PDF: ${error}`);
                    }
                );
            } catch (error) {
                runInAction(() => {
                    this.loading = false;
                    message.error(`Ошибка при формировании PDF: ${error}`);
                });
            }
        }
    }

    @action
    regeneratePdf(): void {
        if (this.id) {
            this.loading = true;
            try {
                this.ticketService.regeneratePdf(
                    this.id,
                    data => {
                        this.loading = false;
                        this.pdfLink = this.filesService.getFileUrl(data["id"]);
                        message.success('PDF успешно перегенерирован');
                    },
                    (error: string) => {
                        this.loading = false;
                        message.error(`Ошибка при формировании PDF: ${error}`);
                    }
                );
            } catch (error) {
                runInAction(() => {
                    this.loading = false;
                    message.error(`Ошибка при формировании PDF: ${error}`);
                });
            }
        }
    }
}